import { useQueryClient, type UseQueryOptions } from 'react-query'

import {
  queryKeys as queryKeysPS,
  handleError,
  validateStatus,
} from 'api/kinerja/pengawas-sekolah/config'
import { apiPaths, queryKeys } from 'api/kinerja/tim-penilai-ps/config'
import { useMutationApi, useQueryApi } from 'utils/api'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'

import type {
  TPraktikKinerjaFormName,
  TSubmissionResponse,
  TDokumenAkuntabilitasResponse,
} from 'types/kinerja-ps-pegawai'
import type { AxiosError } from 'axios'
import type {
  PelaksanaanPSListFilterState,
  TPelaksanaanPSList,
  TPelaksanaanPSListSummary,
  TPelaksanaanPSSummary,
  TPenilaianPerilakuKerja,
  TPenilaianPerilakuKerjaOptions,
  TPenilaianPerilakuKerjaSubmissionBody,
  TPenilaianPerilakuKerjaSubmissionResponse,
  TSubmitRatingDiskusiRefleksiTindakLanjutBody,
  TPenilaianKSSurveyResults,
} from 'types/kinerja-tim-penilai-ps'

export function useAPIGetPelaksanaanPerilakuKerjaTK({
  skpPsId,
  ...options
}: UseQueryOptions<TPenilaianPerilakuKerja> & { skpPsId: string }) {
  return useQueryApi<TPenilaianPerilakuKerja>(
    `${apiPaths.pelaksanaan}/perilaku-kerja/tk/skp-ps/${skpPsId}`,
    {
      queryKey: [queryKeys.API_GET_PELAKSANAAN_PERILAKU_KERJA_TK, skpPsId],
      axiosConfig: {
        validateStatus,
      },
      queryConfig: {
        useErrorBoundary: true,
        ...options,
      },
      errorHandlerFn: handleError,
    }
  )
}

export function useAPIGetPelaksanaanPerilakuKerjaPenilaianOptions({
  ...options
}: UseQueryOptions<TPenilaianPerilakuKerjaOptions>) {
  return useQueryApi<TPenilaianPerilakuKerjaOptions>(
    `${apiPaths.pelaksanaan}/perilaku-kerja/penilaian/options`,
    {
      queryKey: [queryKeys.API_GET_PELAKSANAAN_PERILAKU_KERJA_TK_OPTIONS],
      axiosConfig: {
        validateStatus,
      },
      queryConfig: {
        useErrorBoundary: true,
        ...options,
      },
      errorHandlerFn: handleError,
    }
  )
}

export function useAPISubmitPenilaianPerilakuKerja() {
  const queryClient = useQueryClient()

  return useMutationApi<
    TPenilaianPerilakuKerjaSubmissionResponse,
    AxiosError,
    TPenilaianPerilakuKerjaSubmissionBody
  >(`${apiPaths.pelaksanaan}/perilaku-kerja`, {
    axiosConfig: { method: 'POST' },
    mutationConfig: {
      onSettled(_res, _err, { skpPsId }) {
        queryClient.invalidateQueries([
          queryKeys.API_GET_PELAKSANAAN_PERILAKU_KERJA_TK,
          skpPsId,
        ])
      },
    },
  })
}

export function useAPIGetPelaksanaanPSListSummary(
  options?: UseQueryOptions<TPelaksanaanPSListSummary>
) {
  const { period } = usePeriodContext()

  return useQueryApi<TPelaksanaanPSListSummary>(
    apiPaths.pelaksanaanListSummary(period.id),
    {
      queryKey: [queryKeys.API_GET_PELAKSANAAN_LIST_SUMMARY, period.id],
      axiosConfig: {
        validateStatus,
        ...options,
      },
      errorHandlerFn: handleError,
    }
  )
}

export function useAPIGetPelaksanaanPSList({
  filter,
  ...options
}: {
  filter: PelaksanaanPSListFilterState
} & UseQueryOptions<TPelaksanaanPSList>) {
  const { period } = usePeriodContext()

  return useQueryApi<TPelaksanaanPSList>(
    apiPaths.pelaksanaanList(period.id, filter),
    {
      queryKey: [queryKeys.API_GET_PELAKSANAAN_LIST, period.id, filter],
      axiosConfig: {
        validateStatus,
      },
      queryConfig: {
        useErrorBoundary: false,
        ...options,
      },
      errorHandlerFn: handleError,
    }
  )
}

export function useAPIGetPelaksanaanPSSummary({
  skpPsId,
  ...options
}: {
  skpPsId: string
} & UseQueryOptions<TPelaksanaanPSSummary>) {
  return useQueryApi<TPelaksanaanPSSummary>(
    apiPaths.pelaksanaanSummary(skpPsId),
    {
      queryKey: [queryKeys.API_GET_PELAKSANAAN_SUMMARY, skpPsId],
      axiosConfig: {
        validateStatus,
        ...options,
      },
      errorHandlerFn: handleError,
    }
  )
}

export const useApiSubmitRatingDiskusiTindakLanjut = ({
  skpPsId,
}: {
  skpPsId: string
}) => {
  const queryClient = useQueryClient()
  const formName: TPraktikKinerjaFormName = 'diskusi_tindak_lanjut'

  return useMutationApi<
    TSubmissionResponse,
    AxiosError,
    TSubmitRatingDiskusiRefleksiTindakLanjutBody
  >(
    `${apiPaths.pelaksanaan}/praktik-kinerja/skp-ps/${skpPsId}/rating/diskusi-tindak-lanjut`,
    {
      axiosConfig: { method: 'POST' },
      mutationConfig: {
        onSettled() {
          queryClient.invalidateQueries([
            queryKeysPS.API_GET_PELAKSANAAN_FORM_PRAKTIK_KINERJA,
            skpPsId,
            formName,
          ])
          queryClient.invalidateQueries([
            queryKeysPS.API_GET_PRAKTIK_KINERJA_PELAKSANAAN_SUMMARY,
            skpPsId,
          ])
        },
      },
    }
  )
}

export function useAPISubmitRatingRefleksiTindakLanjut(skpPsId: string) {
  const queryClient = useQueryClient()
  const formName = 'refleksi_tindak_lanjut'

  return useMutationApi<
    TSubmissionResponse,
    AxiosError,
    TSubmitRatingDiskusiRefleksiTindakLanjutBody
  >(apiPaths.praktikKinerjaRatingRefleksi(skpPsId), {
    axiosConfig: { method: 'POST' },
    mutationConfig: {
      onSuccess() {
        return Promise.all([
          queryClient.invalidateQueries([
            queryKeysPS.API_GET_PELAKSANAAN_FORM_PRAKTIK_KINERJA,
            skpPsId,
            formName,
          ]),
          queryClient.invalidateQueries([
            queryKeysPS.API_GET_PRAKTIK_KINERJA_PELAKSANAAN_SUMMARY,
            skpPsId,
          ]),
        ])
      },
    },
  })
}

export function useAPIGetKSSurveyResults({
  skpPsId,
  ...options
}: {
  skpPsId: string
} & UseQueryOptions<TPenilaianKSSurveyResults>) {
  return useQueryApi<TPenilaianKSSurveyResults>(
    `${apiPaths.pelaksanaanPraktikKinerja(skpPsId)}/rating/survey-ks-hasil`,
    {
      queryKey: [
        queryKeys.API_GET_PELAKSANAAN_PRAKTIK_KINERJA_HASIL_SURVEY_KS,
        skpPsId,
      ],
      axiosConfig: {
        validateStatus,
      },
      queryConfig: {
        useErrorBoundary: false,
        ...options,
      },
      errorHandlerFn: handleError,
    }
  )
}

// Get Dokumen Akuntabilitas
export const useApiGetDokumenAkuntabilitas = ({
  skpPsId,
  opts,
}: {
  skpPsId: string
  opts?: UseQueryOptions<TDokumenAkuntabilitasResponse>
}) => {
  return useQueryApi(`${apiPaths.dokumenAkuntabilitas(skpPsId)}`, {
    axiosConfig: { validateStatus },
    queryConfig: { useErrorBoundary: false, ...opts },
    queryKey: [queryKeys.API_GET_DOKUMEN_AKUNTABILITAS, skpPsId],
  })
}

// Post Dokumen Akuntabilitas
export const useApiPostDokumenAkuntabilitas = ({
  skpPsId,
}: {
  skpPsId: string
}) => {
  const queryClient = useQueryClient()

  return useMutationApi<TSubmissionResponse, AxiosError, {}>(
    `${apiPaths.dokumenAkuntabilitas(skpPsId)}`,
    {
      axiosConfig: { method: 'POST' },
      mutationConfig: {
        onSettled() {
          queryClient.invalidateQueries([
            queryKeys.API_GET_DOKUMEN_AKUNTABILITAS,
            skpPsId,
          ])
        },
      },
    }
  )
}
